import { ArrowRight, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Container } from '../../components/ui/Container';
import { PageHero } from '../../components/ui/PageHero';
import { pb } from '../../lib/pb';

/**
 * Branded landing page for PocketBase's email-change confirmation link. The
 * user has requested an email change; PB sent a confirmation message to the
 * NEW address with this URL. We collect their current password (PB requires
 * it as proof of identity for the change) and POST the confirm endpoint.
 */
export function ConfirmEmailChange(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorText, setErrorText] = useState<string>('');

  useEffect(() => {
    const previous = document.title;
    document.title = 'Confirm new email | Sunset Harbor Condominiums';
    return () => {
      document.title = previous;
    };
  }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!token || !password) return;
    setSubmitting(true);
    setState('idle');
    setErrorText('');
    try {
      await pb.collection('users').confirmEmailChange(token, password);
      setState('success');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      console.error(err);
      setState('error');
      setErrorText(
        err instanceof Error && err.message
          ? err.message
          : 'That link is invalid or has expired, or the password was wrong.',
      );
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <>
        <PageHero
          eyebrow="Account"
          title="Confirm new email"
          lead="The link we used to get here is missing a token."
        />
        <section className="section bg-brand-white">
          <Container>
            <div className="mx-auto max-w-md rounded-2xl border border-brand-surface bg-brand-white p-8 shadow-card md:p-10">
              <p className="text-sm text-brand-body-dark">
                Please open the link from your email again, or contact the board.
              </p>
              <Link
                to="/contact"
                className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-md border border-brand-surface bg-brand-white px-5 text-sm font-semibold text-brand-charcoal hover:bg-brand-surface"
              >
                Contact the board
              </Link>
            </div>
          </Container>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Account"
        title="Confirm new email"
        lead="Enter your current password to finish changing the email address on your Sunset Harbor account."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mx-auto max-w-md rounded-2xl border border-brand-surface bg-brand-white p-8 shadow-card md:p-10">
            {state === 'success' ? (
              <div className="flex items-start gap-3">
                <span
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal"
                  aria-hidden="true"
                >
                  <CheckCircle2 size={22} strokeWidth={2} />
                </span>
                <div>
                  <h2 className="text-xl font-bold text-brand-charcoal">Email updated</h2>
                  <p className="mt-2 text-sm leading-relaxed text-brand-body-dark">
                    Redirecting you to sign in with the new address...
                  </p>
                </div>
              </div>
            ) : null}

            {state === 'error' ? (
              <div
                role="alert"
                className="mb-6 flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                <XCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>{errorText}</span>
              </div>
            ) : null}

            {state !== 'success' ? (
              <form onSubmit={onSubmit} noValidate>
                <label className="block">
                  <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                    Current password
                  </span>
                  <input
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                    className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                  />
                </label>

                <button
                  type="submit"
                  disabled={submitting || password.length === 0}
                  className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-brand-yellow px-5 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-yellow-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      Confirm new email
                      <ArrowRight size={16} aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>
            ) : null}
          </div>
        </Container>
      </section>
    </>
  );
}
